import * as React from 'react';
import styled from 'styled-components';
import { rem } from 'polished';
import { ILeadFormUIProps } from 'types/ILeadFormUIProps';
import { Input, Button, Textarea, ErrorMessage } from '@makemydeal/ui-bricks/dist/cox';
import { IShopper } from '@makemydeal/dr-platform-types';
import { BreakPoint, IconFactory } from '@makemydeal/dr-activities-common';
import {
    validateEmailWithDomainFilter,
    validateFirstName,
    validateLastName,
    validatePhone,
    validateRequiredField
} from '@makemydeal/dr-common-utils';
import { charactersRemaining } from '../utils/charactersRemaining';
import { SpinnerUI, TCPATemplatesUI } from '@makemydeal/dr-activities-common';
import { normalizePhone } from '../utils';
import { KeyboardEvent, useRef } from 'react';

const { useState, useEffect } = React;
const TRANSITION_MILISECONDS = 800;
const MAX_MESSAGE_LENGTH = 500;

enum ButtonState {
    DEFAULT = 'DEFAULT',
    SENDING = 'SENDING',
    SUCCESS = 'SUCCESS',
    FAILURE = 'FAILURE'
}

interface IErrorMessage {
    hasError?: boolean;
}

const blurReportableFields = ['firstName', 'lastName', 'phone', 'email', 'message'] as const;
type BlurReportableField = (typeof blurReportableFields)[number];

const DisclaimerSection = styled.div<IErrorMessage>`
    display: flex;
    flex-direction: column;
    font-size: ${rem(12)};
    color: ${({ theme, hasError }) => {
        if (hasError) return theme.errorMessageTextColor;
        return theme.leadFormContainerDisclaimerColor;
    }};
    @media (min-width: ${BreakPoint.SMALL}) {
        font-size: ${rem(14)};
        margin: ${rem(8)};
    }
`;
const CCPASection = styled.div<IErrorMessage>`
    display: flex;
    font-size: ${rem(12)};
    margin-top: 1rem;
    color: ${({ theme, hasError }) => {
        if (hasError) return theme.errorMessageTextColor;
        return theme.leadFormContainerDisclaimerColor;
    }};
    @media (min-width: ${BreakPoint.SMALL}) {
        font-size: ${rem(14)};
        margin: ${rem(8)};
    }
`;

const LeadFormContainer = styled.form`
    padding: 16px;
    background-color: ${({ theme }) => theme.leadFormContainerBackgroundColor};
    height: 100%;
    font-family: ${({ theme }) => theme.primaryFontFamily || 'Open Sans'};
    border-radius: 8px;
`;

const LeadFormRow = styled.div`
    display: flex;
    flex-direction: column;
    @media (min-width: ${BreakPoint.SMALL}) {
        flex-direction: row;
    }
`;

const LeadFormTitle = styled.div`
    font-size: ${rem(24)};
    color: ${({ theme }) => theme.leadFormTitleColor};
    margin: ${rem(8)} 0;

    @media (min-width: ${BreakPoint.SMALL}) {
        margin: ${rem(8)};
    }
`;

const LeadFormDescription = styled.div`
    font-size: ${rem(14)};
    color: ${({ theme }) => theme.leadFormDescriptionColor};
    margin: ${rem(8)} 0;
    margin-bottom: 2rem;

    @media (min-width: ${BreakPoint.SMALL}) {
        margin: ${rem(8)};
        margin-bottom: 2rem;
    }
`;

const LeadFormSubmitError = styled.div`
    margin: ${rem(8)} 0;
    border: 1px ${({ theme }) => theme.errorMessageTextColor} solid;
    padding: ${rem(8)} 0 ${rem(8)} ${rem(8)};

    & > div {
        margin: 0;
    }

    @media (min-width: ${BreakPoint.SMALL}) {
        margin: ${rem(8)};
    }
`;

const LeadFormSubLabel = styled.span`
    color: ${({ theme }) => theme.leadFormSubLabelColor};
    font-weight: normal;
`;

const LeadFormInputWrapper = styled.div`
    label {
        font-family: ${({ theme }) => theme.labelFieldFontFamily};
    }
    @media (min-width: ${BreakPoint.SMALL}) {
        width: 50%;
        margin: 0 ${rem(8)};
    }
`;

const LeadFormCommentWrapper = styled.div`
    margin-bottom: ${rem(16)};
    label {
        font-family: ${({ theme }) => theme.labelFieldFontFamily};
        font-weight: ${({ theme }) => theme.labelFieldFontWeight};
    }
    @media (min-width: ${BreakPoint.SMALL}) {
        margin: 0 ${rem(8)} ${rem(16)} ${rem(8)};
    }
`;

const MessageLengthCounter = styled.div`
    font-size: ${rem(14)};
    color: ${({ theme }) => theme.leadFormMessageLengthCounterColor};
    text-align: right;
`;

const LeadFormButtonWrapper = styled.div`
    box-sizing: border-box;
    display: flex;
    width: 100%;
    flex-direction: column;
    justify-content: center;
    margin-top: ${rem(40)};
    padding-bottom: 2rem;

    @media (min-width: ${BreakPoint.SMALL}) {
        align-items: center;
    }

    .dr-sp-spinner {
        position: relative;
        top: 43px;
        right: 3px;
    }
`;

const LeadFormSkipLink = styled.button`
    align-self: center;
    margin: ${rem(16)} 0;
    font-size: ${rem(16)};
    font-weight: ${({ theme }) => theme.leadFormSkipLinkFontWeight};
    border-style: unset;
    cursor: pointer;

    :disabled {
        cursor: unset;
        color: ${({ theme }) => theme.primaryButtonDisabledBackgroundColor};
    }

    @media (min-width: ${BreakPoint.SMALL}) {
        font-size: ${rem(18)};
    }
`;

const SubmitButton = styled(Button as any)`
    min-width: 226px;
    min-height: 50px;
    font-family: ${({ theme }) => theme.defaultButtonFontFamily};
    background-color: ${({ theme, status }) => {
        if (status === ButtonState.DEFAULT) return theme.primaryButtonColor;
        if (status === ButtonState.SENDING) return theme.primaryButtonDisabledBackgroundColor;
        if (status === ButtonState.FAILURE) return theme.primaryButtonFailureBackgroundColor;
        if (status === ButtonState.SUCCESS) return theme.primaryButtonSuccessBackgroundColor;
    }};
    :hover {
        background-color: ${({ theme, status }) => {
            if (status === ButtonState.DEFAULT) return theme.primaryButtonHoverColor;
            if (status === ButtonState.SENDING) return theme.primaryButtonDisabledBackgroundColor;
            if (status === ButtonState.FAILURE) return theme.primaryButtonFailureBackgroundColor;
            if (status === ButtonState.SUCCESS) return theme.primaryButtonSuccessBackgroundColor;
        }};
    }
`;

const WhiteIcon = styled(IconFactory)`
    svg {
        width: 27px;
        height: 20px;
        g {
            path {
                fill: ${({ theme }) => theme.leadFormWhiteIcon};
            }
        }
    }
`;

const ConsentError = styled.div`
    margin: ${rem(8)} 0;
    padding: ${rem(8)} 0 ${rem(8)} ${rem(8)};

    & > div {
        margin: 0;
    }

    @media (min-width: ${BreakPoint.SMALL}) {
        margin: ${rem(8)};
    }
`;

const RemoveTrade = styled.a`
    text-decoration: none;
    margin-left: ${rem(3)};
    color: ${({ theme }) => theme.primaryLinkColor};

    ::hover {
        text-decoration: underline;
        color: ${({ theme }) => theme.primaryLinkHoverColor};
    }
`;

const timeout = () => new Promise((res) => setTimeout(res, TRANSITION_MILISECONDS));

export const LeadFormUI: React.FC<ILeadFormUIProps> = ({
    shopper,
    leadFormSubmitting,
    leadFormSubmittedSuccess,
    leadFormSubmittedFailure,
    leadFormReinitialized,
    isLeadFormOptional,
    isTCPAEnabled,
    isTCPACheckboxDisabled,
    isTCPAInitChecked,
    CCPAConsentText,
    title,
    description,
    successAction,
    meta,
    staticImages,
    externalErrorRetries,
    errorDescription,
    preSubmitAction,
    onSubmit,
    onLeadFormSubmittedSuccess,
    onFirstNameStart,
    onFirstNameBlur,
    onLastNameBlur,
    onPhoneBlur,
    onEmailBlur,
    onMessageBlur,
    routeWithoutSubmitting,
    backToDeal,
    onReinitializeForm,
    dispatchPreSubmitEvent,
    dismissPaymentTermsFallbackMessage,
    isPhoneNumberRequired,
    zipCodeLoading,
    leadFormCCPAShown,
    isPaymentTermsFallback,
    isTradePaymentFailed,
    dealerPhoneNumber,
    removeTradeInFromSummary,
    isPhoneRequiredForConsentDisplay,
    creditApplicationDisclaimer,
    isShowCreditApplicationDisclaimer,
    dealerName,
    shopperTCPAProvider,
    cdlInitialFormClicked,
    cdlSubmitFormClicked,
    cdlCheckboxChecked,
    isActive,
    shouldRenderDealerElitePackageLeadForm,
    ecommerceTenantURL
}) => {
    const [shopperInformation, setShopperInformation] = useState({
        firstName: shopper?.firstName || '',
        lastName: shopper?.lastName || '',
        zip: shopper?.zip || '',
        phone: shopper?.phone || '',
        pixallId: shopper?.pixallId || '',
        email: shopper?.email || '',
        contactPreferences: shopper?.contactPreferences || { email: false, sms: false, phone: false },
        message: shopper?.message || '',
        antennaId: shopper?.antennaId || '',
        userConsent: shopper?.userConsent || 'unchecked'
    } as IShopper);
    const [fieldValueChanged, setFieldValueChanged] = useState<Record<BlurReportableField, boolean>>({
        firstName: false,
        lastName: false,
        phone: false,
        email: false,
        message: false
    });
    const [fieldValueChangeReported, setFieldValueChangeReported] = useState<{ [key in BlurReportableField]: boolean }>({
        firstName: false,
        lastName: false,
        phone: false,
        email: false,
        message: false
    });
    const [checked, setCheckboxChecked] = useState(isTCPAInitChecked);
    const [submitButtonValue, setSubmitButtonValue] = useState(<>Continue</>);
    const [submitButtonBackground, setSubmitButtonBackground] = useState(ButtonState.DEFAULT);
    const [skipButtonEnabled, setSkipButtonEnabled] = useState(true);
    const [hasSubmitError, setHasSubmitError] = useState(false);
    const [leadFormSubmitError, setLeadFormError] = useState('An error has occurred.');
    const [isValidFirstName, setIsValidFirstName] = useState(true);
    const [isValidLastName, setIsValidLastName] = useState(true);
    const [isValidEmail, setIsValidEmail] = useState(true);
    const [isValidPhone, setIsValidPhone] = useState(true);
    const [isOnSubmitClicked, setIsOnSubmitClicked] = useState(false);

    const validateEmailAndDomain = (email: string): boolean => validateEmailWithDomainFilter(email).valid;

    const shouldShowDisclaimer = (shopper: IShopper) => {
        return shopper?.phone?.length > 0 || !isPhoneRequiredForConsentDisplay;
    };

    const setSubmitButtonToDefault = () => {
        setSubmitButtonBackground(ButtonState.DEFAULT);
        setSubmitButtonValue(<>Continue</>);
    };

    const handleSecondaryActionErrors = () => {
        if (externalErrorRetries > 0) {
            setSubmitButtonBackground(ButtonState.FAILURE);
            setSubmitButtonValue(<WhiteIcon staticImages={staticImages} icon="close" />);
            setLeadFormError(errorDescription);
            setHasSubmitError(true);
            timeout().then(() => {
                setSubmitButtonToDefault();
            });
        }
    };

    const handleLeadFormInitialize = (success, submitted, failure, reinitialized) => {
        if (!success && !submitted && !failure && !reinitialized) {
            setSubmitButtonValue(<>Continue</>);
            setSkipButtonEnabled(true);
            setHasSubmitError(false);
        }
    };

    const handleSubmitSuccess = (success: boolean, action: string) => {
        if (success) {
            const reportAnalytics = !fieldValueChangeReported.firstName;
            onFirstNameStart(reportAnalytics);
            setSubmitButtonBackground(ButtonState.SUCCESS);
            setSubmitButtonValue(<WhiteIcon staticImages={staticImages} icon="check" />);
            timeout().then(() => {
                onLeadFormSubmittedSuccess(action);
                setSubmitButtonToDefault();
            });
        } else {
            setSubmitButtonToDefault();
        }
    };
    const handleSubmitButtonState = (submitting: boolean) => {
        if (submitting) {
            setSubmitButtonBackground(ButtonState.SENDING);
            setSubmitButtonValue(<>Sending...</>);
        }
    };

    const handleSkipButtonState = (submitting: boolean) => {
        setSkipButtonEnabled(!submitting);
    };
    const handleReinitializeForm = () => {
        onReinitializeForm();
    };

    const handleSubmitError = (failed: boolean) => {
        if (failed) {
            setSubmitButtonBackground(ButtonState.FAILURE);
            setSubmitButtonValue(<WhiteIcon staticImages={staticImages} icon="close" />);
            setHasSubmitError(true);
            timeout().then(() => {
                handleReinitializeForm();
                setSubmitButtonToDefault();
            });
        } else {
            setSubmitButtonToDefault();
        }
    };

    const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            cdlCheckboxChecked();
        }
        setCheckboxChecked(e.target.checked);
    };

    const handleChange = (e) => {
        const key = e.target.name;
        const caret = e.target.selectionStart;
        let value = e.target.value;

        if (key === 'phone') {
            const previousValue = e.target.defaultValue;
            window.requestAnimationFrame(() => {
                e.target.selectionStart = caret + 1;
                e.target.selectionEnd = caret + 1;
            });
            value = normalizePhone(value, previousValue);
        }
        const updatedShopperInformation = { ...shopperInformation, [key]: value };

        if (blurReportableFields.includes(key) && !!value && shopperInformation[key] !== value) {
            setFieldValueChanged({ ...fieldValueChanged, [key]: true });
        }

        if (!shouldShowDisclaimer(updatedShopperInformation)) {
            updatedShopperInformation.userConsent = 'unchecked';
        }
        setShopperInformation(updatedShopperInformation);
    };

    const validateOptionalPhone = (value: string) => {
        if (isPhoneNumberRequired) {
            return validateRequiredField(value) && validatePhone(value);
        }
        return !validateRequiredField(value) || validatePhone(value);
    };

    const updateValidationStates = (shopper: IShopper) => {
        setIsValidFirstName(validateFirstName(shopper.firstName));
        setIsValidLastName(validateLastName(shopper.lastName));
        setIsValidEmail(validateEmailAndDomain(shopper.email));
        setIsOnSubmitClicked(true);
        setIsValidPhone(validateOptionalPhone(shopper.phone));
    };

    const isValid = (shopper: IShopper) => {
        const valid =
            !validateFirstName(shopper.firstName) ||
            !validateLastName(shopper.lastName) ||
            !validateEmailAndDomain(shopper.email) ||
            !validateOptionalPhone(shopper.phone) ||
            (isTCPAEnabled && !isTCPACheckboxDisabled && !checked && shouldShowDisclaimer(shopper))
                ? false
                : true;

        if (!validateFirstName(shopper.firstName)) {
            document.getElementById('firstName__label')?.scrollIntoView();
            document.getElementById('firstName')?.focus();
        } else if (!validateLastName(shopper.lastName)) {
            document.getElementById('lastName__label')?.scrollIntoView();
            document.getElementById('lastName')?.focus();
        } else if (!validateEmailAndDomain(shopper.email)) {
            document.getElementById('email__label')?.scrollIntoView();
            document.getElementById('email')?.focus();
        } else if (!validateOptionalPhone(shopper.phone)) {
            document.getElementById('phone__label')?.scrollIntoView();
            document.getElementById('phone')?.focus();
        }

        updateValidationStates(shopper);
        return valid;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        cdlSubmitFormClicked();

        if (isPaymentTermsFallback) {
            dismissPaymentTermsFallbackMessage();
        }
        if (isValid(shopperInformation) && externalErrorRetries > 0) {
            setHasSubmitError(false);

            if (preSubmitAction) {
                dispatchPreSubmitEvent(preSubmitAction);
            } else {
                onLeadFormSubmittedSuccess(successAction);
            }
        } else if (isValid(shopperInformation)) {
            if (preSubmitAction) {
                dispatchPreSubmitEvent(preSubmitAction);
            } else {
                onSubmit(shopper, meta);
            }
        } else {
            setHasSubmitError(false);
        }
    };

    const resetFieldValueStates = () => {
        setFieldValueChanged({ firstName: false, lastName: false, phone: false, email: false, message: false });
        setFieldValueChangeReported({ firstName: false, lastName: false, phone: false, email: false, message: false });
    };

    const cancelLeadForm = () => {
        resetFieldValueStates();
        backToDeal();
    };

    const skipLeadForm = () => {
        resetFieldValueStates();
        routeWithoutSubmitting();
    };

    const onTabPress = function (e: KeyboardEvent<HTMLButtonElement>, func: () => void) {
        if (e.key === 'Tab' && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            e.stopPropagation();
            func();
        }
    };

    const onShiftTabPress = function (e: KeyboardEvent<HTMLElement>, func: () => void) {
        if (e.key === 'Tab' && e.shiftKey) {
            e.preventDefault();
            e.stopPropagation();
            func();
        }
    };

    const renderDisclaimer = () => {
        const showDisclaimer = shouldShowDisclaimer(shopperInformation);
        const isTCPARequired = isTCPAEnabled && !isTCPACheckboxDisabled && !checked && isOnSubmitClicked;
        const consentError = isTCPARequired && showDisclaimer ? 'Your consent is required if phone number is provided.' : null;

        return (
            <div>
                <DisclaimerSection
                    data-testid="shopper-consent-section"
                    className="shopper-consent-section"
                    hasError={isTCPARequired}
                >
                    <TCPATemplatesUI
                        creditApplicationDisclaimer={creditApplicationDisclaimer}
                        showShopperConsentText={isTCPAEnabled && showDisclaimer}
                        showCreditApplicationDisclaimer={isShowCreditApplicationDisclaimer}
                        showTCPACheckbox={isTCPAEnabled && !isTCPACheckboxDisabled && showDisclaimer}
                        handleCheckbox={handleCheckbox}
                        dealerName={dealerName}
                        shopperTCPAProvider={shopperTCPAProvider}
                        shopperInfoUserConsent={shopperInformation.userConsent}
                        shouldRenderDealerElitePackageLeadForm={shouldRenderDealerElitePackageLeadForm}
                    />
                </DisclaimerSection>

                {consentError && (
                    <ConsentError data-testid="disclaimer-error">
                        <ErrorMessage>{consentError}</ErrorMessage>
                    </ConsentError>
                )}
            </div>
        );
    };

    const renderCCPADisclaimer = () => {
        if (leadFormCCPAShown) return <CCPASection data-testid="disclaimer-ccpa-section">{CCPAConsentText}</CCPASection>;
        return null;
    };

    const handleFieldBlurEvent = (key: BlurReportableField) => {
        const reportAnalytics = fieldValueChanged[key] && !fieldValueChangeReported[key];

        switch (key) {
            case 'firstName':
                onFirstNameBlur(shopperInformation, reportAnalytics);
                break;
            case 'lastName':
                onLastNameBlur(shopperInformation, reportAnalytics);
                break;
            case 'email':
                onEmailBlur(shopperInformation, reportAnalytics);
                break;
            case 'phone':
                onPhoneBlur(shopperInformation, reportAnalytics);
                break;
            case 'message':
                onMessageBlur(shopperInformation, reportAnalytics);
                break;
        }

        if (reportAnalytics) {
            setFieldValueChangeReported({ ...fieldValueChangeReported, [key]: true });
        }
    };

    useEffect(() => {
        if (shopper) {
            shopper.userConsent = checked ? 'checked' : 'unchecked';
        }
        setShopperInformation({ ...shopper });
    }, [shopper, checked]);
    useEffect(() => {
        handleLeadFormInitialize(leadFormSubmittedSuccess, leadFormSubmitting, leadFormSubmittedFailure, leadFormReinitialized);
    }, [leadFormSubmittedSuccess, leadFormSubmitting, leadFormSubmittedFailure]);
    useEffect(() => {
        handleSubmitButtonState(leadFormSubmitting);
        handleSkipButtonState(leadFormSubmitting);
    }, [leadFormSubmitting]);
    useEffect(() => {
        handleSubmitError(leadFormSubmittedFailure);
    }, [leadFormSubmittedFailure]);
    useEffect(() => {
        handleSubmitSuccess(leadFormSubmittedSuccess, successAction);
    }, [leadFormSubmittedSuccess]);
    useEffect(() => {
        handleSecondaryActionErrors();
    }, [externalErrorRetries]);
    useEffect(() => {
        setSubmitButtonValue(shouldRenderDealerElitePackageLeadForm ? <>Send Verification Code</> : <>Continue</>);
        if (shouldRenderDealerElitePackageLeadForm && !ecommerceTenantURL) {
            setLeadFormError('Tenant URL is not configured.');
            setHasSubmitError(true);
        } else {
            setHasSubmitError(false);
            setLeadFormError('An error has occurred.');
        }
    }, [shouldRenderDealerElitePackageLeadForm, ecommerceTenantURL]);

    const firstNameRef = useRef<HTMLInputElement>(null);
    const skipButtonRef = useRef<HTMLButtonElement>(null);
    const cdlEventSentRef = useRef(false);

    useEffect(() => {
        if (isActive) {
            firstNameRef.current?.focus();
        }

        if (!zipCodeLoading) {
            firstNameRef.current?.focus();
        }
    }, [isActive, zipCodeLoading]);

    const focusFirstName = () => {
        firstNameRef?.current?.focus();
    };

    const focusSkipButton = () => {
        skipButtonRef?.current?.focus();
    };

    const renderSkipButton = () => {
        return isLeadFormOptional ? (
            <LeadFormSkipLink
                className="primary-link"
                type="button"
                ref={skipButtonRef}
                disabled={!skipButtonEnabled}
                onClick={skipLeadForm}
                onKeyDown={(e) => onTabPress(e, focusFirstName)}
            >
                No thanks, continue to next step
            </LeadFormSkipLink>
        ) : (
            <LeadFormSkipLink
                className="primary-link"
                type="button"
                ref={skipButtonRef}
                disabled={!skipButtonEnabled}
                onClick={cancelLeadForm}
                onKeyDown={(e) => onTabPress(e, focusFirstName)}
            >
                {shouldRenderDealerElitePackageLeadForm ? 'Cancel' : 'Back To My Deal'}
            </LeadFormSkipLink>
        );
    };

    const handleFocus = () => {
        if (cdlEventSentRef.current) {
            return;
        }
        cdlInitialFormClicked();
        cdlEventSentRef.current = true;
    };

    const remainingCharacters = charactersRemaining(shopperInformation.message, MAX_MESSAGE_LENGTH);

    const tradeErrorMessage = `Oops. We couldn't calculate a monthly payment based on your trade-in value. Try removing your trade to receive a payment or contact us at ${dealerPhoneNumber}.`;
    const TradeErrorMessage = ErrorMessage as any;

    return (
        <LeadFormContainer data-testid="dr-activities-lead-form">
            <LeadFormTitle className="common-header">{title}</LeadFormTitle>
            <LeadFormDescription>{description} All input fields are required, unless marked as optional.</LeadFormDescription>
            {hasSubmitError && (
                <LeadFormSubmitError>
                    <ErrorMessage>{leadFormSubmitError}</ErrorMessage>
                </LeadFormSubmitError>
            )}
            {isTradePaymentFailed && (
                <LeadFormSubmitError data-testid="dr-lead-form-trade-payment-error">
                    <TradeErrorMessage>
                        {tradeErrorMessage}
                        <RemoveTrade onClick={removeTradeInFromSummary} data-testid="dr-lead-form-remove-trade">
                            Remove Trade-In
                        </RemoveTrade>
                    </TradeErrorMessage>
                </LeadFormSubmitError>
            )}
            <LeadFormRow>
                <LeadFormInputWrapper>
                    <Input
                        name="firstName"
                        value={shopperInformation.firstName}
                        label="First Name"
                        type="text"
                        placeholder="e.g. John"
                        onChangeFunction={handleChange}
                        onBlurFunction={() => handleFieldBlurEvent('firstName')}
                        onKeyDownFunction={(e) => onShiftTabPress(e, focusSkipButton)}
                        validationFunction={validateFirstName}
                        hasError={!isValidFirstName}
                        errorMessage="Please enter a valid first name."
                        inputId="firstName"
                        ref={firstNameRef}
                        onFocusFunction={handleFocus}
                    />
                </LeadFormInputWrapper>
                <LeadFormInputWrapper>
                    <Input
                        name="lastName"
                        value={shopperInformation.lastName}
                        label="Last Name"
                        type="text"
                        placeholder="e.g. Smith"
                        onChangeFunction={handleChange}
                        onBlurFunction={() => handleFieldBlurEvent('lastName')}
                        validationFunction={validateLastName}
                        hasError={!isValidLastName}
                        errorMessage="Please enter a valid last name."
                        inputId="lastName"
                        onFocusFunction={handleFocus}
                    />
                </LeadFormInputWrapper>
            </LeadFormRow>
            <LeadFormRow>
                <LeadFormInputWrapper>
                    <Input
                        name="email"
                        value={shopperInformation.email}
                        label="Email"
                        type="text"
                        placeholder="e.g. JSmith@gmail.com"
                        validationFunction={validateEmailAndDomain}
                        errorMessage="Please enter a valid email address."
                        onChangeFunction={handleChange}
                        hasError={!isValidEmail}
                        onBlurFunction={() => handleFieldBlurEvent('email')}
                        inputId="email"
                        onFocusFunction={handleFocus}
                    />
                </LeadFormInputWrapper>
                <LeadFormInputWrapper>
                    <Input
                        name="phone"
                        value={shopperInformation.phone}
                        label={<span>Phone{!isPhoneNumberRequired && <LeadFormSubLabel> (Optional)</LeadFormSubLabel>}</span>}
                        type="text"
                        placeholder="e.g. 456-789-0123"
                        onChangeFunction={handleChange}
                        onBlurFunction={() => handleFieldBlurEvent('phone')}
                        validationFunction={validateOptionalPhone}
                        hasError={!isValidPhone}
                        errorMessage="Please enter a valid phone number."
                        inputId="phone"
                        data-testid="leadform-phone"
                        onFocusFunction={handleFocus}
                    />
                </LeadFormInputWrapper>
            </LeadFormRow>
            {!shouldRenderDealerElitePackageLeadForm && (
                <LeadFormCommentWrapper>
                    <Textarea
                        name="message"
                        maxLength={MAX_MESSAGE_LENGTH}
                        rows={3}
                        value={shopperInformation.message}
                        onChange={handleChange}
                        onBlur={() => handleFieldBlurEvent('message')}
                        disableResize={true}
                        onFocus={handleFocus}
                        label={
                            <div>
                                Comments <LeadFormSubLabel>(Optional)</LeadFormSubLabel>
                            </div>
                        }
                    />
                    <MessageLengthCounter>{`${remainingCharacters} characters remaining`}</MessageLengthCounter>
                </LeadFormCommentWrapper>
            )}
            {renderDisclaimer()}
            {renderCCPADisclaimer()}
            <LeadFormButtonWrapper>
                {zipCodeLoading && <SpinnerUI size="30" />}
                <SubmitButton
                    type="button"
                    role="button"
                    data-testid="leadform-submit"
                    disabled={
                        leadFormSubmitting ||
                        zipCodeLoading ||
                        isTradePaymentFailed ||
                        (shouldRenderDealerElitePackageLeadForm && !ecommerceTenantURL)
                    }
                    onClick={handleSubmit}
                    status={submitButtonBackground}
                >
                    {submitButtonValue}
                </SubmitButton>
                {renderSkipButton()}
            </LeadFormButtonWrapper>
        </LeadFormContainer>
    );
};

export default LeadFormUI;
